import center from "@turf/center"
import area from "@turf/area"
import {
  Geometry,
  Feature,
  Polygon,
  Position,
  polygon,
  MultiPolygon,
} from "@turf/helpers"
import _ from "lodash"

export type Ring = Position[]
export type PolyCoords = Ring[]

// Calculates the center point of a GeoJSON geometry and Return as a Coordinates.
export const getCenterFromGeometry = (geometry?: Geometry): number[] | null => {
  try {
    const { type = null, coordinates = null } = geometry
    if (!type || !coordinates) return null
    const centerPoint = center(geometry)
    return _.get(centerPoint, "geometry.coordinates") // Coordinates
  } catch (error) {
    return null
  }
}

/**
 * Returns the largest exterior boundary as a Polygon feature.
 * Works with Polygon/MultiPolygon.
 */
export const getExteriorBoundaryPolygon = <T = Polygon | MultiPolygon>(
  polygonal?: Feature<T>
): Feature<Polygon> => {
  const geometry = polygonal?.geometry as Polygon | MultiPolygon
  if (
    !geometry ||
    (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")
  ) {
    throw new Error("Input must be a Polygon or MultiPolygon")
  }

  const largestRing =
    geometry.type === "Polygon"
      ? pickLargestRing(geometry.coordinates)
      : pickLargestRing(
          geometry.coordinates.map((poly) =>
            pickLargestRing(poly)
          ) as unknown as PolyCoords
        )

  return polygon([largestRing])
}

/** Pick the largest ring by area from an array of rings. */
export const pickLargestRing = (rings: PolyCoords): Ring => {
  const validRing = rings.filter((r) => Array.isArray(r) && r.length >= 4)
  if (!validRing.length) throw new Error("No valid rings (need ≥ 4 positions).")

  return validRing.reduce((best, ring) =>
    areaOf(ring) > areaOf(best) ? ring : best
  )
}

const areaOf = (ring: Ring) => area(polygon([ring]))

/**
 * Checks if a point is a valid coordinate pair.
 *
 * @param point - The point to be checked.
 * @return True if the point is a valid coordinate pair, false otherwise.
 */
const isValidCoordinate = (point: Position) => {
  return point.length === 2 && point.every((coord) => typeof coord === "number")
}

/**
 * Checks if a set of points forms a valid linear ring (closed and simple polygon).
 *
 * @param ring - The array of points representing a linear ring.
 * @return True if the linear ring is valid, false otherwise.
 */
export const isValidLinearRing = (ring: Position[]): boolean => {
  if (ring.length < 4) {
    return false
  }

  return (
    ring.every(isValidCoordinate) &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  )
}

/**
 * Checks if an input represents a valid polygon, which may include holes.
 * The input can be a single array of coordinates for simple polygons, or
 * an array of arrays of coordinates for polygons with holes.
 *
 * @param polygon - The input representing a polygon.
 * @return True if the polygon is valid, false otherwise.
 */
export const isValidPolygon = (polygon: Position[] | Position[][]): boolean => {
  // Check if it's a simple polygon (without holes)
  if (
    Array.isArray(polygon[0]) &&
    (!polygon[0].length || typeof polygon[0][0] === "number")
  ) {
    return isValidLinearRing(polygon as Position[])
  }

  // Check if it's a polygon with holes
  if (
    Array.isArray(polygon) &&
    polygon.length > 0 &&
    Array.isArray(polygon[0])
  ) {
    // Check the outer ring
    if (!isValidLinearRing(polygon[0] as Position[])) {
      return false
    }

    // Check each hole
    for (let i = 1; i < polygon.length; i++) {
      if (!isValidLinearRing(polygon[i] as Position[])) {
        return false
      }
    }

    return true
  }

  return false
}
